import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api, { getErrorMessage } from '@/hooks/useApi';

// Types matching backend responses
export interface FeatureToggle {
  toggleId: string;
  type: 'ops' | 'config' | 'release';
  enabled: boolean;
  config?: Record<string, unknown>;
  description: string;
  updatedAt: string;
  updatedBy: string;
}

export interface UpdateToggleInput {
  toggleId: string;
  enabled?: boolean;
  config?: Record<string, unknown>;
  description?: string;
}

// Query keys for cache management
export const toggleKeys = {
  all: ['toggles'] as const,
  admin: () => [...toggleKeys.all, 'admin'] as const,
};

/**
 * Fetch all toggles with full metadata (admin only)
 */
export function useAdminToggles() {
  return useQuery({
    queryKey: toggleKeys.admin(),
    queryFn: async () => {
      const response = await api.get<{ toggles: FeatureToggle[] }>('/admin/toggles');
      return response.data.toggles;
    },
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Update a toggle (admin only)
 */
export function useUpdateToggle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ toggleId, ...updates }: UpdateToggleInput) => {
      const response = await api.patch<{ toggle: FeatureToggle }>(
        `/admin/toggles/${toggleId}`,
        updates
      );
      return response.data.toggle;
    },
    onSuccess: () => {
      // Invalidate the admin toggles list
      queryClient.invalidateQueries({ queryKey: toggleKeys.admin() });
    },
    onError: (error) => {
      console.error('Update toggle failed:', getErrorMessage(error));
    },
  });
}
